import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Row, Col, Table } from "react-bootstrap";
import {
  IoAddOutline,
  IoChevronDownOutline,
  IoDocumentTextOutline,
} from "react-icons/io5";
import GetData from "../../../services/getData";
import Toast from "../../../components/layout/toast/Toast";
import "../patients.css";
import "./patientDocs.css";

const DOCUMENT_TYPE_OPTIONS = [
  { id: "1", labelKey: "layout.profileDocs.primary", path: "primary" },
  { id: "2", labelKey: "layout.profileDocs.repeat", path: "repeat" },
  { id: "3", labelKey: "layout.profileDocs.anamnesis", path: "anamnesis" },
  { id: "4", labelKey: "layout.profileDocs.anketa", path: "anketa" },
  { id: "22", labelKey: "layout.profileDocs.smart-diagnostics", path: "smart-diagnostics" },
];

function formatDateForDisplay(isoDate) {
  if (!isoDate) return "—";
  const [datePart] = String(isoDate).trim().split(/[ T]/);
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "—";
  return `${day}.${month}.${year}`;
}

function sortDocsNewestFirst(docs) {
  return [...docs].sort((a, b) => {
    const da = Date.parse(a?.created_date) || 0;
    const db = Date.parse(b?.created_date) || 0;
    if (db !== da) return db - da;
    return (Number(b?.id) || 0) - (Number(a?.id) || 0);
  });
}

function PatientProfileList() {
  const { t } = useTranslation("patient");
  const navigate = useNavigate();
  const { patientId } = useParams();
  const [docsList, setDocsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({});
  const [blocked, setBlocked] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const addMenuRef = useRef(null);

  useEffect(() => {
    if (!showAddMenu) return undefined;
    const handleClickOutside = (event) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        setShowAddMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAddMenu]);

  const sortedDocsList = useMemo(
    () => sortDocsNewestFirst(docsList),
    [docsList],
  );

  const documentTypes = useMemo(
    () =>
      DOCUMENT_TYPE_OPTIONS.map((option) => ({
        ...option,
        name: t(option.labelKey),
      })),
    [t],
  );

  // Тип документа (path) по template_id или по самому документу
  const getDocTypePath = (doc) =>
    doc?.path ||
    documentTypes.find(
      (item) => String(item.id) === String(doc?.template_id),
    )?.path ||
    "";

  const docTabs = [
    { key: "all", label: t("profileList.tabs.all") },
    { key: "anamnesis", label: t("profileList.tabs.anamnesis") },
    { key: "anketa", label: t("profileList.tabs.anketa") },
  ];

  const visibleDocsList =
    activeTab === "all"
      ? sortedDocsList
      : sortedDocsList.filter(
          (doc) => getDocTypePath(doc) === activeTab,
        );

  const loadDocsList = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await GetData({
        module: "patient",
        block: "patient_docs",
        id_patient: patientId,
        action: "list",
      });
      setDocsList(data.result || []);
    } catch (err) {
      console.error("Ошибка загрузки документов медкарты:", err);
      setError(err.message || t("profileList.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // Создание документа выбранного типа (вызывается из выпадающего меню)
  const createDoc = async (selected) => {
    if (!selected?.path || blocked) return;
    setBlocked(true);
    try {
      const response = await GetData({
        module: "patient",
        block: "patient_docs",
        id_patient: patientId,
        action: "create",
        template_id: Number(selected.id),
      });

      if (response?.status === "fail") {
        setToast({
          id: Date.now(),
          title: response.message || t("profileList.errors.createFailed"),
          status: "error",
        });
        return;
      }

      const createdDocId =
        response?.result?.id?.value || response?.new_doc_id || null;

      setToast({
        id: Date.now(),
        title: response.message || t("profileList.created"),
        status: response.status || "success",
      });

      await loadDocsList();

      if (createdDocId) {
        navigate(`/patient/${patientId}/profile/${selected.path}/${createdDocId}`);
      }
    } catch (err) {
      console.error("Ошибка создания документа медкарты:", err);
      setToast({
        id: Date.now(),
        title: t("profileList.errors.createFailed"),
        status: "error",
      });
    } finally {
      setBlocked(false);
    }
  };

  const getDocPath = (doc) => {
    const path =
      doc?.path ||
      documentTypes.find((item) => String(item.id) === String(doc?.template_id))
        ?.path;
    if (!path || !doc?.id) return null;
    return `/patient/${patientId}/profile/${path}/${doc.id}`;
  };

  if (loading) return <div className="loading">{t("loading")}</div>;
  if (error) return <div className="error">{t("error", { ns: "common", message: error })}</div>;

  return (
    <div className="pd">
      {toast.title ? (
        <Toast key={toast.id} title={toast.title} status={toast.status} />
      ) : (
        false
      )}

      {/* Кнопка «Добавить» — раскрывающийся список типов документов */}
      <div className="pd-toolbar">
        <div className="pd-tags" role="tablist">
          {docTabs.map((tabItem) => (
            <button
              key={tabItem.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tabItem.key}
              className={`pd-tag${activeTab === tabItem.key ? " is-active" : ""}`}
              onClick={() => setActiveTab(tabItem.key)}
            >
              {tabItem.label}
            </button>
          ))}
        </div>

        <div className="pd-add" ref={addMenuRef}>
          <button
            type="button"
            className="pd-add__btn"
            onClick={() => setShowAddMenu((prev) => !prev)}
            aria-expanded={showAddMenu}
            disabled={blocked}
          >
            <IoAddOutline size={17} aria-hidden="true" />
            {t("profileList.add")}
            <IoChevronDownOutline
              size={14}
              aria-hidden="true"
              className={`pd-add__caret${showAddMenu ? " is-open" : ""}`}
            />
          </button>
          {showAddMenu ? (
            <div className="pd-add__menu" role="menu">
              {documentTypes.map((docType) => (
                <button
                  key={docType.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setShowAddMenu(false);
                    createDoc(docType);
                  }}
                >
                  <IoDocumentTextOutline size={17} aria-hidden="true" />
                  {docType.name}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {visibleDocsList.length > 0 ? (
        <Row>
          <Col>
            <div className="table-list">
              <Table className="main-table-list rounded-table">
                <thead>
                  <tr>
                    <th>{t("profileList.colType")}</th>
                    <th>{t("profileList.colCreated")}</th>
                    <th>{t("profileList.colAuthor")}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleDocsList.map((doc) => {
                    const to = getDocPath(doc);
                    return (
                      <tr key={doc.id}>
                        <td>
                          {to ? (
                            <Link to={to} className="table-item-link">
                              <div className="table-item-name">
                                {doc.template_name || t("profileList.document")}
                              </div>
                            </Link>
                          ) : (
                            <div className="table-item-name">
                              {doc.template_name || t("profileList.document")}
                            </div>
                          )}
                        </td>
                        <td>{formatDateForDisplay(doc.created_date)}</td>
                        <td>{doc.create_user_name || "—"}</td>
                      </tr>
                    );
                  })}
                  <tr className="table-summary">
                    <td colSpan={3} className="summary-cell">
                      {t("profileList.found", { count: visibleDocsList.length })}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      ) : (
        <div className="no-patients">
          {activeTab === "all"
            ? t("profileList.empty")
            : t("profileList.emptyTab")}
        </div>
      )}
    </div>
  );
}

export default PatientProfileList;
